import React from "react";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import VisibilityIcon from "@mui/icons-material/Visibility";

import { Partner } from "../../api/partners";
import { usePartnersStore } from "../../store/partnersStore";
import { useDialogState } from "../../hooks/useDialogState";
import { InfinitePaginationTable } from "../common/InfinitePaginationTable";
import { PartnerDetailDialog } from "./PartnerDetailDialog";

export const PartnerList = () => {
  const { partners, loading, loadPartners } = usePartnersStore();
  const detailDialog = useDialogState<Partner>();

  React.useEffect(() => {
    loadPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      id: "displayName",
      label: "Partner",
      render: (partner: Partner) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={partner.logoUrl}
            alt={partner.displayName}
            sx={{ width: 28, height: 28, bgcolor: partner.primaryColor }}
          >
            {partner.displayName.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2">{partner.displayName}</Typography>
        </Box>
      ),
    },
    {
      id: "hashtag",
      label: "Tag",
      render: (partner: Partner) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          #{partner.hashtag}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (partner: Partner) => {
        const isActive = partner.status === "Active";
        return (
          <Chip
            label={partner.status}
            size="small"
            sx={{
              backgroundColor: isActive ? "success.light" : "action.disabledBackground",
              color: isActive ? "success.dark" : "text.secondary",
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      id: "actions",
      label: "Actions",
      render: (partner: Partner) => (
        <Tooltip title="View details">
          <IconButton
            size="small"
            onClick={() => detailDialog.openWith(partner)}
            sx={{ color: "#1976d2" }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const sortedPartners = [...partners].sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <InfinitePaginationTable
        columns={columns}
        items={sortedPartners}
        pageSize={10}
        loading={loading}
        onLoadNext={() => {}}
      />
      <PartnerDetailDialog
        open={detailDialog.open}
        partner={detailDialog.data}
        onClose={detailDialog.close}
      />
    </Box>
  );
};
